/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import * as size from '@constant/size';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';
import Gap from '@component/common/gap';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { ETheme } from '@datatype/type';
import { dismissNoticeByDay } from '@hook/iyagigo/useNotification';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${size.MEDIUM_MODAL_WIDTH};
`;

const PriceContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: 100%;
  padding: ${size.MARGIN_1};
  background-color: ${color.BROWN_4};
  border-radius: ${size.BORDER_RADIUS_1};
`;

const HideForWeekContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  width: 100%;
`;

const TITLE_DESIGN = {
  text: { size: 'large', color: color.BLACK_2, bold: true },
  box: { alignX: 'center', width: '100%' },
} as const;

const CONTENT_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: { alignX: 'center', width: '100%' },
} as const;

const CONTENT_SUB_TEXT_DESIGN = {
  text: { size: 'medium', color: color.GRAY_1, bold: false },
  box: { alignX: 'center', width: '100%' },
} as const;

const PRICE_LABEL_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: true },
} as const;

const PRICE_START_DATE_DESIGN = {
  text: { size: 'small', color: color.GRAY_1, bold: false, underline: false },
} as const;

const PRICE_VALUE_DESIGN = {
  text: { size: 'medium', color: color.BROWN_1, bold: true },
} as const;

const DISMISS_TEXT_DESIGN = {
  text: { size: 'small', color: color.GRAY_1, bold: false, underline: true },
  box: { event: { cursor: 'pointer' } },
} as const;

const SubscriptionPlanLaunchModal: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();

  const handleViewClick = () => {
    dispatch(modalActions.hideModal());
    navigate(IYAGIGO_PATH.HUB_SUBSCRIPTION_PATH);
  };

  const handleDismissClick = () => {
    dismissNoticeByDay();
    dispatch(modalActions.hideModal());
  };

  return (
    <Wrapper>
      <TextBlock text='💌 죄송한 소식 전해드려요' design={TITLE_DESIGN} />
      <Gap height={size.MARGIN_2} />
      <TextBlock text='더 좋은 서비스를 오래 만들어가기 위해' design={CONTENT_TEXT_DESIGN} />
      <TextBlock text='<b>6월 1일</b>부터 구독 플랜이 시작돼요.' design={CONTENT_TEXT_DESIGN} />
      <Gap height={size.MARGIN_1} />
      <TextBlock text='무료로도 가볍게' design={CONTENT_SUB_TEXT_DESIGN} />
      <TextBlock text='학습을 즐기실 수 있어요.' design={CONTENT_SUB_TEXT_DESIGN} />
      <Gap height={size.MARGIN_2} />
      <PriceContainer>
        <TextBlock text='이야기고 Plus⁺' design={PRICE_LABEL_DESIGN} />
        <Gap height='4px' />
        <TextBlock text='6월 1일 시작' design={PRICE_START_DATE_DESIGN} />
        <Gap height='4px' />
        <TextBlock text='월 5,900원 / 연 39,000원' design={PRICE_VALUE_DESIGN} />
      </PriceContainer>
      <Gap height={size.MARGIN_2} />
      <TextButton
        text='자세히 보기'
        theme={ETheme.BROWN}
        size='medium'
        shape='pill'
        width='100%'
        handleClick={handleViewClick}
      />
      <Gap height={size.MARGIN_1} />
      <HideForWeekContainer>
        <TextBlock text='일주일간 보지 않기' design={DISMISS_TEXT_DESIGN} click={{ handler: handleDismissClick }} />
      </HideForWeekContainer>
    </Wrapper>
  );
};

export default SubscriptionPlanLaunchModal;
